// ============================================================================
// FinanceModals — Personal Finance V2 (Blueprint §5.2)
// Bundle de modais auxiliares extraídos de PersonalFinance.jsx:
//   - DrillDownModal (drill-down na visão anual)
//   - CategoryManagerModal (gerenciar categorias + metas)
//   - InitialBalanceModal (saldo inicial dia zero)
// ============================================================================

import React, { useState } from 'react';
import { DEFAULT_CATEGORIES } from '../domain/categories.js';
import { SaveCancelBar } from '../../../../components/ui/SaveCancelBar';

const formatCurrency = (val) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);

export const DrillDownModal = ({ onClose, data, title, monthLabel }) => {
    return (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden border-t-4 border-blue-500">
                <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                    <div>
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">{monthLabel}</p>
                        <h3 className="text-lg font-bold text-gray-800">{title}</h3>
                    </div>
                    <button onClick={onClose}><i className="fas fa-times text-gray-400 hover:text-red-500 text-lg"></i></button>
                </div>
                <div className="p-0 max-h-96 overflow-y-auto">
                    {data.items.length === 0 ? (
                        <div className="p-8 text-center text-gray-400 text-sm">Nenhum lançamento compõe este valor.</div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 text-xs text-gray-500 uppercase font-bold sticky top-0">
                                <tr>
                                    <th className="p-3 text-left">Descrição</th>
                                    <th className="p-3 text-left">Conta</th>
                                    <th className="p-3 text-right">Valor</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {data.items.map((item, idx) => (
                                    <tr key={idx} className="hover:bg-blue-50">
                                        <td className="p-3">
                                            <div className="font-bold text-gray-700">{item.description}</div>
                                            <div className="text-[10px] text-gray-400">{item.category}</div>
                                        </td>
                                        <td className="p-3 text-xs text-gray-500 font-medium">
                                            {item.account || '-'}
                                        </td>
                                        <td className="p-3 text-right font-mono font-bold text-gray-700">
                                            {formatCurrency(item.value)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot className="bg-gray-50 font-bold border-t border-gray-200">
                                <tr>
                                    <td colSpan="2" className="p-3 text-right text-gray-600">TOTAL</td>
                                    <td className="p-3 text-right text-brand-blue">{formatCurrency(data.total)}</td>
                                </tr>
                            </tfoot>
                        </table>
                    )}
                </div>
                <div className="p-3 bg-gray-50 text-center border-t border-gray-100">
                    <button onClick={onClose} className="text-xs font-bold text-gray-500 hover:text-gray-800">FECHAR</button>
                </div>
            </div>
        </div>
    );
};

export const CategoryManagerModal = ({ onClose, categories, onSaveCategories }) => {
    const [localCats, setLocalCats] = useState(categories || DEFAULT_CATEGORIES);
    const [newSub, setNewSub] = useState('');
    const [selectedGroup, setSelectedGroup] = useState('RECEITAS');

    const handleAddSub = () => {
        if (!newSub.trim()) return;
        const updated = { ...localCats };
        if (!updated[selectedGroup].subs) updated[selectedGroup].subs = [];
        updated[selectedGroup].subs.push(newSub);
        setLocalCats({ ...updated });
        setNewSub('');
    };

    const handleDeleteSub = (group, sub) => {
        const updated = { ...localCats };
        if (updated[group]?.subs) {
            updated[group].subs = updated[group].subs.filter(s => s !== sub);
            setLocalCats({ ...updated });
        }
    };
    
    const handleBudgetChange = (group, value) => {
         const updated = { ...localCats };
         updated[group].budget = Number(value);
         setLocalCats({ ...updated });
    };

    const handleSave = () => {
        onSaveCategories(localCats);
        onClose();
    };

    const currentSubs = localCats[selectedGroup]?.subs || [];
    const currentBudget = localCats[selectedGroup]?.budget || 0;

    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden border-t-4 border-blue-600">
                <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                    <h3 className="text-lg font-bold text-gray-800">Gerenciar Categorias & Metas</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-red-500"><i className="fas fa-times text-xl"></i></button>
                </div>
                <div className="p-6 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Selecione o Grupo</label>
                            <select 
                                className="w-full p-2.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                                value={selectedGroup}
                                onChange={e => setSelectedGroup(e.target.value)}
                            >
                                {Object.entries(localCats).map(([key, val]) => (
                                    <option key={key} value={key}>{val.label}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                             <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Teto Mensal (Meta)</label>
                             <input 
                                type="number"
                                className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                value={currentBudget}
                                onChange={e => handleBudgetChange(selectedGroup, e.target.value)}
                             />
                        </div>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 h-64 overflow-y-auto custom-scrollbar">
                        <p className="text-xs font-bold text-gray-500 mb-2 uppercase tracking-wide">Subcategorias Atuais</p>
                        <div className="space-y-2">
                            {currentSubs.map((sub, idx) => (
                                <div key={idx} className="flex justify-between items-center bg-white p-3 rounded-md border border-gray-100 shadow-sm hover:border-blue-200">
                                    <span className="text-sm text-gray-700 font-medium">{sub}</span>
                                    <button onClick={() => handleDeleteSub(selectedGroup, sub)} className="text-gray-300 hover:text-red-500"><i className="fas fa-trash"></i></button>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="flex gap-2">
                        <input 
                            data-scb-ignore-enter="true"
                            className="flex-1 p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" 
                            placeholder="Nova Subcategoria..." 
                            value={newSub}
                            onChange={e => setNewSub(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleAddSub()}
                        />
                        <button onClick={handleAddSub} className="bg-blue-600 text-white px-4 rounded-lg text-sm font-bold hover:bg-blue-700 shadow-sm">Adicionar</button>
                    </div>
                </div>
                <div className="p-4 bg-gray-50 border-t border-gray-100">
                    <SaveCancelBar
                        onCancel={onClose}
                        onSave={handleSave}
                        cancelLabel="Cancelar"
                        saveLabel="Salvar e Sincronizar"
                    />
                </div>
            </div>
        </div>
    );
};

export const InitialBalanceModal = ({ onClose, onSave, currentValue }) => {
    const [balance, setBalance] = useState(currentValue || 0);

    return (
        <div className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden border-t-4 border-indigo-600">
                <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                    <h3 className="text-lg font-bold text-gray-800">Saldo Inicial (Dia Zero)</h3>
                    <button onClick={onClose}><i className="fas fa-times text-gray-400 hover:text-red-500"></i></button>
                </div>
                <div className="p-6 space-y-4">
                    <div className="bg-indigo-50 p-3 rounded text-xs text-indigo-800 mb-2">
                        Insira o saldo total atual das suas contas para calibrar o gráfico de liquidez.
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Saldo Atual (Todas as Contas)</label>
                        <input 
                            type="number" 
                            step="0.01"
                            className="w-full p-3 border border-gray-300 rounded-lg text-lg font-bold text-gray-700 focus:ring-2 focus:ring-indigo-500 outline-none text-center" 
                            value={balance} 
                            onChange={e => setBalance(e.target.value)} 
                        />
                    </div>
                </div>
                <div className="p-4 bg-gray-50 border-t border-gray-100">
                    <SaveCancelBar
                        onCancel={onClose}
                        onSave={() => onSave(balance)}
                        cancelLabel="Cancelar"
                        saveLabel="Salvar"
                    />
                </div>
            </div>
        </div>
    );
};
